import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { mountScrollWorld } from '../lib/scroll-world/scrub-engine';
import { installVariantToggle, restoreFlightPosition } from '../lib/scroll-world/variant-toggle';
import { installFlightTracking } from '../lib/flight-analytics';
import { BETA_COPY, primaryCta } from '../lib/signup';

// A/B experiment Variant B — "Night Shift": scroll-scrubbed camera flight through
// the unseen team's overnight studio. Not prerendered, noindexed.
// Assets are served from a CDN (Supabase Storage public bucket — CORS `*` + HTTP
// range requests + a 1-year immutable cache), keeping ~140 MB of video out of the
// repo (#211). Override with VITE_FLIGHT_CDN_BASE to point at a different CDN.
const FLIGHT_BASE =
  import.meta.env.VITE_FLIGHT_CDN_BASE ||
  'https://qdvlzcqejhyceqrluzvl.supabase.co/storage/v1/object/public/marketing-flight';
const ASSETS = `${FLIGHT_BASE}/night-shift`;

const TOPBAR_CTA = primaryCta('flight_b_topbar');
const FINAL_CTA = primaryCta('flight_b_final');

// Exported so FlightLanding can prerender the scene copy for crawlers.
// eslint-disable-next-line react-refresh/only-export-components
export const CONFIG = {
  brand: { name: 'lykbl', href: '/' },
  cta: { label: TOPBAR_CTA.shortLabel, href: TOPBAR_CTA.href },
  hint: 'scroll to meet the night shift',
  diveScroll: 1.35,
  sections: [
    {
      id: 'dusk',
      label: 'Dusk',
      still: `${ASSETS}/still1.jpg`,
      clip: `${ASSETS}/scene1.mp4`,
      clipMobile: `${ASSETS}/m/scene1.mp4`,
      accent: '#0e7c6e',
      scroll: 1.5,
      linger: 0.35,
      eyebrow: 'Your AI agent team · you approve every move',
      title: 'Grow your audience. Turn attention into offers.',
      body: 'A team of AI specialists that researches your audience, drafts your content, and shapes your offers — brand pitches, bookings, and paid communities. Brief them before you log off; the drafts are waiting when you’re back.',
      tags: ['Review-first by default', 'Official APIs only — no scraping', 'Tuned to your voice'],
    },
    {
      id: 'signals',
      label: 'Signals',
      still: `${ASSETS}/still2.jpg`,
      clip: `${ASSETS}/scene2.mp4`,
      clipMobile: `${ASSETS}/m/scene2.mp4`,
      accent: '#0e7c6e',
      eyebrow: 'Command centre',
      title: 'Your platforms, one command centre.',
      body: 'Stop juggling dashboards. Connect supported channels to see their signals side by side — with one queue for everything your agents prepare. More platforms are on the way.',
      tags: ['YouTube', 'LinkedIn', 'TikTok', 'X', 'More coming'],
    },
    {
      id: 'drafts',
      label: 'Drafts',
      still: `${ASSETS}/still3.jpg`,
      clip: `${ASSETS}/scene3.mp4`,
      clipMobile: `${ASSETS}/m/scene3.mp4`,
      accent: '#c46c2a',
      eyebrow: 'How it works',
      title: 'Agents do the work. You make the calls.',
      body: 'Specialist agents research topics, write content, shape offers, and prepare replies — handing work to each other like a real team, guided by your brief. Everything starts stamped DRAFT and waits for your yes.',
      tags: ['01 Brief the team once', '02 Agents draft your moves', '03 You approve every move'],
    },
    {
      id: 'review',
      label: 'Review',
      still: `${ASSETS}/still4.jpg`,
      clip: `${ASSETS}/scene4.mp4`,
      clipMobile: `${ASSETS}/m/scene4.mp4`,
      accent: '#cdf355',
      eyebrow: 'You set the autonomy',
      title: 'Every draft queues for your yes.',
      body: 'Review every output yourself — nothing is published or sent without your approval. Earned autonomy for proven workflows is on the roadmap.',
      tags: ['Queued for your review', 'Nothing sends without you'],
    },
    {
      id: 'revenue',
      label: 'Revenue',
      still: `${ASSETS}/still5.jpg`,
      clip: `${ASSETS}/scene5.mp4`,
      clipMobile: `${ASSETS}/m/scene5.mp4`,
      accent: '#c46c2a',
      eyebrow: 'Earn',
      title: 'Keep every opportunity in one place.',
      body: 'Track brand deals, bookings and course sign-ups in one revenue desk, and let an agent draft the follow-up in your voice. The outgoing slot stays latched until you say so.',
      tags: ['Brand deals', 'Bookings & consults', 'Communities & courses'],
    },
    {
      id: 'dawn',
      label: 'Dawn',
      still: `${ASSETS}/still6.jpg`,
      clip: `${ASSETS}/scene6.mp4`,
      clipMobile: `${ASSETS}/m/scene6.mp4`,
      accent: '#cdf355',
      scroll: 1.7,
      linger: 0.45,
      eyebrow: 'Nothing sent. Everything ready.',
      title: 'Approve the night’s work.',
      body: `Every draft, reply, and post waits in one queue for your yes. Autonomy is earned, not assumed. ${BETA_COPY.pitch}`,
      tags: [],
      cta: {
        primary: { label: FINAL_CTA.label, href: FINAL_CTA.href },
        secondary: { label: 'Find out more', href: '/home' },
      },
    },
  ],
  // Frame-locked aerial connectors (one per seam, length = sections - 1): each was
  // generated from the previous scene's last frame → next scene's first frame, so
  // the seams are pixel-identical. Set any entry to null to fall back to a crossfade.
  connectors: [
    `${ASSETS}/conn1.mp4`,
    `${ASSETS}/conn2.mp4`,
    `${ASSETS}/conn3.mp4`,
    `${ASSETS}/conn4.mp4`,
    `${ASSETS}/conn5.mp4`,
  ],
  // Lighter 720p tight-GOP encodes served on phones (engine picks these on
  // coarse-pointer / narrow viewports).
  connectorsMobile: [
    `${ASSETS}/m/conn1.mp4`,
    `${ASSETS}/m/conn2.mp4`,
    `${ASSETS}/m/conn3.mp4`,
    `${ASSETS}/m/conn4.mp4`,
    `${ASSETS}/m/conn5.mp4`,
  ],
};

export default function FlightB({ landing = false }: { landing?: boolean } = {}) {
  const worldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = worldRef.current;
    if (!el || el.dataset.swMounted) return;
    el.dataset.swMounted = 'true';
    mountScrollWorld(el, CONFIG);
    // Replace the engine's generic brand mark with the official lykbl wordmark
    // (same asset + treatment as BrandLogo.tsx: white PNG, unfiltered on dark).
    const brand = el.querySelector('.sw-brand');
    if (brand) {
      brand.innerHTML =
        '<span class="sw-brand__wm"><img src="/lykbl_logo.png" alt="lykbl" class="sw-brand__logo" /><sup>™</sup></span>' +
        '<span class="sw-brand__cue">say it: “likeable”</span>';
    }
    installVariantToggle(el, 'b');
    restoreFlightPosition();
    installFlightTracking(el, 'b');
    // The engine owns document scroll and window listeners and has no unmount API.
    // Every exit link it renders is a plain <a>, so leaving this route is always a
    // full document navigation and no SPA cleanup is needed. Revisit before this
    // page ever becomes router-linked from elsewhere.
  }, []);

  return (
    <>
      <Helmet>
        <title>{landing ? 'lykbl — AI agent teams that grow your audience and shape your offers' : 'lykbl — your agents work the night shift'}</title>
        <meta name="description" content="Scroll through the studio behind the wall: an AI agent team that drafts your growth and offer work and queues everything for your approval." />
        <meta name="robots" content={landing ? 'index, follow' : 'noindex, nofollow'} />
        <link rel="canonical" href="https://lykbl.com/" />
        <style>{`
          /* .sw-root included: the engine's own (layered) .sw-root defaults set these
             custom properties directly on the container, which beats values inherited
             from :root — an unlayered .sw-root rule is what actually overrides them. */
          :root, .sw-root {
            --sw-bg: #101413;
            --sw-ink: #f5f3ee;
            --sw-ink-soft: #b8b4a9;
            --sw-accent: #0e7c6e;
            --sw-font-display: 'Space Grotesk', system-ui, sans-serif;
            --sw-font-body: 'DM Sans', system-ui, sans-serif;
          }
          html, body { background: #101413; }
          /* The app-level chat widget (fixed bottom-right) sits over the flight's
             hint/route UI — hide it on this immersive route only. */
          #root > div.fixed.bottom-6.right-6 { display: none; }
          /* Dark theme: the engine paints CTAs white-on-ink, which turns
             white-on-cream once --sw-ink is light. Use the brand act color. */
          .sw-topcta, .sw-btn--primary { background: #cdf355; color: #171e1c; }
          .sw-btn--ghost { color: #f5f3ee; border-color: rgba(245, 243, 238, 0.35); }
          .sw-route__label { color: #171e1c; }
          /* Top nav: the engine's light-glass pill washes out over bright scenes.
             Dark glass + paper text; active = paper pill with ink text (readable
             over every scene, independent of the per-scene accent). */
          .sw-nav { background: rgba(13, 17, 16, 0.6); border-color: rgba(245, 243, 238, 0.14); }
          .sw-nav__item { color: rgba(245, 243, 238, 0.72); }
          .sw-nav__item:hover { color: #f5f3ee; }
          .sw-nav__item.is-active { color: #101413; background: #f5f3ee; }
          /* Real brand wordmark (white PNG, shown unfiltered on dark) replaces the
             engine's generic mark — see brand swap in the mount effect. Keeps the
             site's "say it: likeable" pronunciation cue beside it. */
          .sw-brand__wm { display: inline-flex; align-items: flex-start; }
          .sw-brand__logo { height: 28px; width: auto; object-fit: contain; }
          .sw-brand__wm sup { font-size: 8px; color: rgba(245, 243, 238, 0.5); margin-top: 2px; margin-left: 1px; }
          .sw-brand__cue { padding-left: 12px; border-left: 1px solid rgba(245, 243, 238, 0.22); font-size: 0.8rem; color: rgba(245, 243, 238, 0.55); white-space: nowrap; }
          @media (max-width: 720px) { .sw-brand__cue { display: none; } }
          /* Dawn/Night variant switcher (dark theme). */
          .sw-topright { display: flex; align-items: center; gap: 12px; }
          .sw-vt { display: flex; gap: 2px; padding: 3px; border-radius: 999px; background: rgba(245, 243, 238, 0.12); }
          .sw-vt a { text-decoration: none; font-size: 0.82rem; font-weight: 600; padding: 7px 14px; border-radius: 999px; color: rgba(245, 243, 238, 0.7); transition: color 0.2s; }
          .sw-vt a:hover { color: #f5f3ee; }
          .sw-vt a.is-active { background: #f5f3ee; color: #101413; }
        `}</style>
      </Helmet>
      <h1 className="sr-only">Grow your audience. Turn attention into offers. AI agent teams that work the night shift and queue everything for your approval.</h1>
      <div ref={worldRef} />
    </>
  );
}
